import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { IconSearch, IconUpload, IconDownload } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../lib/roles';
import './Tenders.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const proposalListSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const PROPOSALS_BUCKET = 'proposals';

function dateStr(v) {
  if (!v) return '';
  return typeof v === 'string' ? v.slice(0, 10) : v;
}

function proposalFileDisplayName(stored) {
  if (!stored) return '';
  const s = String(stored);
  return s.includes('/') ? s.split('/').pop() : s;
}

const FILE_NAME_MAX = 18;

function truncateFileName(name) {
  if (!name) return '';
  const n = String(name);
  if (n.length <= FILE_NAME_MAX) return n;
  return n.slice(0, FILE_NAME_MAX) + '…';
}

export default function ProposalList() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const admin = isAdmin(role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [dateSortAsc, setDateSortAsc] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      proposalListSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    setError(null);
    await runWakeUp();
    if (admin) {
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
        );
        const [tendersRes, proposalsRes] = await Promise.race([
          Promise.all([
            proposalListSupabase
              .from('tenders')
              .select('id, tender_id, title, issuing_authority, submission_deadline, proposal_submission_deadline, assigned_to_user_id')
              .order('created_at', { ascending: false }),
            proposalListSupabase
              .from('proposals')
              .select('id, tender_id, assigned_to_user_id, commercial_proposal_name, technical_proposal_name, proposal_amount, estimated_cost, delivery_duration, result')
              .order('created_at', { ascending: false }),
          ]),
          timeoutPromise,
        ]);
        const err = tendersRes.error || proposalsRes.error;
        if (err) {
          setError(err.message || tendersRes.error?.message || proposalsRes.error?.message);
          setRows([]);
        } else {
          const tenders = tendersRes.data || [];
          const proposals = proposalsRes.data || [];
          const proposalByTenderId = Object.fromEntries((proposals || []).map((p) => [p.tender_id, p]));
          const mapped = tenders.map((t) => {
            const p = proposalByTenderId[t.id];
            return {
              id: p ? p.id : `tender-${t.id}`,
              tenderId: t.id,
              tenderRef: t.tender_id || '',
              tenderTitle: t.title || '',
              client: t.issuing_authority || '',
              submissionDeadline: dateStr(t.submission_deadline) || '',
              assignmentDeadline: dateStr(t.proposal_submission_deadline) || '',
              assignedToUserId: t.assigned_to_user_id || null,
              commercialProposalName: p?.commercial_proposal_name ?? '',
              technicalProposalName: p?.technical_proposal_name ?? '',
              proposalAmount: p?.proposal_amount != null ? String(p.proposal_amount) : '',
              estimatedCost: p?.estimated_cost != null ? String(p.estimated_cost) : '',
              deliveryDuration: p?.delivery_duration ?? '',
              result: p?.result ?? '',
            };
          });
          setRows(mapped);
        }
      } catch (e) {
        setError(e?.message || 'Failed to load');
        setRows([]);
      }
      setLoading(false);
      return;
    }
    const { data, error: err } = await proposalListSupabase
        .from('proposals')
        .select(`
          id,
          tender_id,
          assigned_to_user_id,
          commercial_proposal_name,
          technical_proposal_name,
          proposal_amount,
          estimated_cost,
          delivery_duration,
          result,
          tenders (
            tender_id,
            title,
            issuing_authority,
            submission_deadline,
            proposal_submission_deadline
          )
        `)
        .order('created_at', { ascending: false });
      if (err) {
        setError(err.message);
        setRows([]);
      } else {
        const mapped = (data || []).map((p) => {
          const t = p.tenders || {};
          return {
            id: p.id,
            tenderId: p.tender_id,
            tenderRef: t.tender_id || '',
            tenderTitle: t.title || '',
            client: t.issuing_authority || '',
            submissionDeadline: dateStr(t.submission_deadline) || '',
            assignmentDeadline: dateStr(t.proposal_submission_deadline) || '',
            assignedToUserId: null,
            commercialProposalName: p.commercial_proposal_name || '',
            technicalProposalName: p.technical_proposal_name || '',
            proposalAmount: p.proposal_amount != null ? String(p.proposal_amount) : '',
            estimatedCost: p.estimated_cost != null ? String(p.estimated_cost) : '',
            deliveryDuration: p.delivery_duration || '',
            result: p.result || '',
          };
        });
        setRows(mapped);
      }
    setLoading(false);
  }, [admin, runWakeUp]);

  const refreshSessionAndFetch = useCallback(async () => {
    setError(null);
    try {
      await proposalListSupabase.auth.getSession();
      await runWakeUp();
      await fetchProposals();
    } catch (e) {
      setError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchProposals]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { error: sessionErr } = await proposalListSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchProposals();
    })();
    return () => { cancelled = true; };
  }, [runWakeUp, fetchProposals]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const updateRow = useCallback((proposalId, key, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === proposalId ? { ...row, [key]: value } : row))
    );
  }, []);

  const saveProposalField = useCallback(
    async (proposalId, field, value) => {
      const col = {
        commercialProposalName: 'commercial_proposal_name',
        technicalProposalName: 'technical_proposal_name',
        proposalAmount: 'proposal_amount',
        estimatedCost: 'estimated_cost',
        deliveryDuration: 'delivery_duration',
        result: 'result',
      }[field];
      if (!col) return;
      const dbValue =
        col === 'proposal_amount' || col === 'estimated_cost'
          ? value === '' || value == null
            ? null
            : Number(value)
          : value === '' ? null : value;
      setSavingId(proposalId);
      await runWakeUp();
      const isNewRow = typeof proposalId === 'string' && proposalId.startsWith('tender-');
      if (isNewRow && admin) {
        const tenderId = proposalId.replace(/^tender-/, '');
        const row = rows.find((r) => r.id === proposalId);
        const { data: inserted, error: insertErr } = await proposalListSupabase
          .from('proposals')
          .insert({
            tender_id: tenderId,
            assigned_to_user_id: row?.assignedToUserId ?? null,
            [col]: dbValue,
            updated_at: new Date().toISOString(),
          })
          .select('id')
          .single();
        setSavingId(null);
        if (insertErr) {
          setError(insertErr.message);
          setTimeout(() => setError(null), 3000);
          return;
        }
        setRows((prev) =>
          prev.map((r) => (r.id === proposalId ? { ...r, id: inserted.id } : r))
        );
        return;
      }
      const { error: err } = await proposalListSupabase
        .from('proposals')
        .update({ [col]: dbValue, updated_at: new Date().toISOString() })
        .eq('id', proposalId);
      setSavingId(null);
      if (err) {
        setError(err.message);
        setTimeout(() => setError(null), 3000);
      }
    },
    [admin, rows, runWakeUp]
  );

  const uploadProposalFile = useCallback(
    async (proposalId, key, file) => {
      const field = key === 'commercialProposalName' ? 'commercial_proposal_name' : 'technical_proposal_name';
      const subdir = key === 'commercialProposalName' ? 'commercial' : 'technical';
      let resolvedId = proposalId;
      await runWakeUp();
      const isNewRow = typeof proposalId === 'string' && proposalId.startsWith('tender-');
      if (isNewRow && admin) {
        const tenderId = proposalId.replace(/^tender-/, '');
        const row = rows.find((r) => r.id === proposalId);
        const { data: inserted, error: insertErr } = await proposalListSupabase
          .from('proposals')
          .insert({
            tender_id: tenderId,
            assigned_to_user_id: row?.assignedToUserId ?? null,
            updated_at: new Date().toISOString(),
          })
          .select('id')
          .single();
        if (insertErr) {
          setError(insertErr.message);
          setSavingId(null);
          return;
        }
        resolvedId = inserted.id;
        setRows((prev) =>
          prev.map((r) => (r.id === proposalId ? { ...r, id: inserted.id } : r))
        );
      }
      const safeName = (file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${resolvedId}/${subdir}/${Date.now()}_${safeName}`;
      const { error: uploadErr } = await proposalListSupabase.storage
        .from(PROPOSALS_BUCKET)
        .upload(storagePath, file, { upsert: true });
      if (uploadErr) {
        setError(uploadErr.message || 'Upload failed.');
        setSavingId(null);
        return;
      }
      const { error: updateErr } = await proposalListSupabase
        .from('proposals')
        .update({ [field]: storagePath, updated_at: new Date().toISOString() })
        .eq('id', resolvedId);
      setSavingId(null);
      if (updateErr) {
        setError(updateErr.message);
        return;
      }
      updateRow(resolvedId, key, storagePath);
      setError(null);
      setUploadSuccess('Uploaded. Use Download to open the file.');
      setTimeout(() => setUploadSuccess(null), 3000);
    },
    [admin, rows, updateRow, runWakeUp]
  );

  const handleFileChange = (proposalId, key, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setSavingId(proposalId);
    updateRow(proposalId, key, file.name);
    uploadProposalFile(proposalId, key, file);
  };

  const handleDownloadProposalFile = useCallback(async (storagePath) => {
    if (!storagePath) return;
    await runWakeUp();
    const { data, error: urlErr } = await proposalListSupabase.storage.from(PROPOSALS_BUCKET).createSignedUrl(storagePath, 60);
    if (urlErr) {
      setError(urlErr.message || 'Download failed.');
      setTimeout(() => setError(null), 4000);
      return;
    }
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  }, [runWakeUp]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = rows
      .filter(
        (row) =>
          !q ||
          (row.tenderRef || '').toLowerCase().includes(q) ||
          (row.tenderTitle || '').toLowerCase().includes(q) ||
          (row.client || '').toLowerCase().includes(q)
      )
      .filter((row) => !resultFilter || row.result === resultFilter);

    return [...base].sort((a, b) => {
      const da = a.submissionDeadline || '';
      const db = b.submissionDeadline || '';
      if (!da && !db) return 0;
      if (!da) return dateSortAsc ? 1 : -1;
      if (!db) return dateSortAsc ? -1 : 1;
      if (da === db) return 0;
      return dateSortAsc ? (da < db ? -1 : 1) : (da > db ? -1 : 1);
    });
  }, [rows, search, resultFilter, dateSortAsc]);

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const from = totalCount === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const to = Math.min(currentPage * perPage, totalCount);

  const handleExportExcel = () => {
    const header = [
      'Tender Ref',
      'Client',
      'Submission deadline',
      'Deadline',
      'Commercial Proposal',
      'Technical Proposal',
      'Proposal Amount',
      'Estimated Cost',
      'Delivery Duration',
      'Result',
    ];
    const csvRows = filtered.map((r) => [
      r.tenderRef,
      r.client,
      r.submissionDeadline,
      r.assignmentDeadline,
      proposalFileDisplayName(r.commercialProposalName),
      proposalFileDisplayName(r.technicalProposalName),
      r.proposalAmount,
      r.estimatedCost,
      r.deliveryDuration,
      r.result,
    ]);
    const csv = [header, ...csvRows]
      .map((row) => row.map((v) => (String(v).includes(',') ? `"${String(v).replace(/"/g, '""')}"` : v)).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'proposals-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="tenders-page-card">
        <div className="tenders-page-header">
          <h2 className="tenders-page-title">Proposal List</h2>
        </div>
        <p className="tenders-muted">Loading proposals…</p>
      </div>
    );
  }

  return (
    <div className="tenders-page-card">
      <div className="tenders-page-header">
        <h2 className="tenders-page-title">Proposal List</h2>
      </div>
      {error && (
        <p className="tenders-error" role="alert">
          {error}
        </p>
      )}
      {uploadSuccess && (
        <p className="tenders-muted" style={{ color: 'var(--success, green)' }} role="status">
          {uploadSuccess}
        </p>
      )}

      <div className="tenders-filter-row">
        <div className="tenders-filter-left">
          <select
            className="tenders-filter-select"
            value={resultFilter}
            onChange={(e) => {
              setResultFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Result</option>
            <option value="Awarded">Awarded</option>
            <option value="Lost">Lost</option>
          </select>
        </div>
        <div className="tenders-filter-right">
          <button type="button" className="tenders-utility-btn" onClick={handleExportExcel}>
            Excel
          </button>
          <div className="tenders-search">
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <span className="tenders-search-icon"><IconSearch /></span>
          </div>
        </div>
      </div>

      <div className="tenders-table-wrapper proposals-table-wrapper">
        <table className="tenders-table proposals-table">
          <thead>
            <tr>
              <th className="proposals-th-tender">Tender</th>
              <th className="proposals-th-date">Deadline</th>
              <th className="proposals-th-file">Commercial</th>
              <th className="proposals-th-file">Technical</th>
              <th className="proposals-th-num">Amount</th>
              <th className="proposals-th-num">Est. cost</th>
              <th className="proposals-th-duration">Delivery</th>
              <th className="proposals-th-result">Result</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={8} className="tenders-empty-cell">
                  No proposals found.
                </td>
              </tr>
            ) : (
              paginated.map((row) => (
                <tr key={row.id}>
                  <td className="proposals-td-tender">
                    <button
                      type="button"
                      className="tenders-table-link-btn"
                      onClick={() => navigate(`/tenders/${row.tenderId}`)}
                    >
                      {row.tenderRef}
                    </button>
                    <div className="proposals-tender-title">{row.tenderTitle}</div>
                  </td>
                  <td className="proposals-td-date">{row.assignmentDeadline || '—'}</td>
                  <td className="proposals-td-file">
                    <input
                      type="file"
                      id={`commercial-upload-${row.id}`}
                      className="proposals-file-input"
                      onChange={(e) => handleFileChange(row.id, 'commercialProposalName', e.target.files)}
                    />
                    <span
                      className="proposals-file-label"
                      title={proposalFileDisplayName(row.commercialProposalName) || undefined}
                    >
                      {truncateFileName(proposalFileDisplayName(row.commercialProposalName)) || '—'}
                    </span>
                    <span className="proposals-file-actions">
                      <button
                        type="button"
                        className="proposals-icon-btn"
                        onClick={() => document.getElementById(`commercial-upload-${row.id}`)?.click()}
                        disabled={savingId === row.id}
                        title="Upload commercial proposal"
                        aria-label="Upload commercial proposal"
                      >
                        <IconUpload />
                      </button>
                      {row.commercialProposalName && row.commercialProposalName.includes('/') && (
                        <button
                          type="button"
                          className="proposals-icon-btn"
                          onClick={() => handleDownloadProposalFile(row.commercialProposalName)}
                          title="Download"
                          aria-label="Download commercial proposal"
                        >
                          <IconDownload />
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="proposals-td-file">
                    <input
                      type="file"
                      id={`technical-upload-${row.id}`}
                      className="proposals-file-input"
                      onChange={(e) => handleFileChange(row.id, 'technicalProposalName', e.target.files)}
                    />
                    <span
                      className="proposals-file-label"
                      title={proposalFileDisplayName(row.technicalProposalName) || undefined}
                    >
                      {truncateFileName(proposalFileDisplayName(row.technicalProposalName)) || '—'}
                    </span>
                    <span className="proposals-file-actions">
                      <button
                        type="button"
                        className="proposals-icon-btn"
                        onClick={() => document.getElementById(`technical-upload-${row.id}`)?.click()}
                        disabled={savingId === row.id}
                        title="Upload technical proposal"
                        aria-label="Upload technical proposal"
                      >
                        <IconUpload />
                      </button>
                      {row.technicalProposalName && row.technicalProposalName.includes('/') && (
                        <button
                          type="button"
                          className="proposals-icon-btn"
                          onClick={() => handleDownloadProposalFile(row.technicalProposalName)}
                          title="Download"
                          aria-label="Download technical proposal"
                        >
                          <IconDownload />
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="proposals-td-num">
                    <input
                      type="number"
                      className="tenders-form-input proposals-input-num"
                      placeholder="—"
                      value={row.proposalAmount}
                      onChange={(e) => updateRow(row.id, 'proposalAmount', e.target.value)}
                      onBlur={(e) => saveProposalField(row.id, 'proposalAmount', e.target.value)}
                    />
                  </td>
                  <td className="proposals-td-num">
                    <input
                      type="number"
                      className="tenders-form-input proposals-input-num"
                      placeholder="—"
                      value={row.estimatedCost}
                      onChange={(e) => updateRow(row.id, 'estimatedCost', e.target.value)}
                      onBlur={(e) => saveProposalField(row.id, 'estimatedCost', e.target.value)}
                    />
                  </td>
                  <td className="proposals-td-duration">
                    <input
                      type="text"
                      className="tenders-form-input proposals-input-text"
                      placeholder="—"
                      value={row.deliveryDuration}
                      onChange={(e) => updateRow(row.id, 'deliveryDuration', e.target.value)}
                      onBlur={(e) => saveProposalField(row.id, 'deliveryDuration', e.target.value)}
                    />
                  </td>
                  <td className="proposals-td-result">
                    <select
                      className="tenders-form-select proposals-select"
                      value={row.result}
                      onChange={(e) => {
                        const v = e.target.value;
                        updateRow(row.id, 'result', v);
                        saveProposalField(row.id, 'result', v || null);
                      }}
                    >
                      <option value="">—</option>
                      <option value="Awarded">Awarded</option>
                      <option value="Lost">Lost</option>
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="tenders-footer-row">
        <div className="tenders-footer-left">
          <select
            className="tenders-page-size"
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span>{from}-{to} / {totalCount}</span>
        </div>
        <div className="tenders-footer-right">
          <button
            type="button"
            className="tenders-page-btn"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            «
          </button>
          <button type="button" className="tenders-page-btn" disabled>
            {currentPage}
          </button>
          <button
            type="button"
            className="tenders-page-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}
